import React, { useState, useEffect, useCallback } from 'react';
import { Card, Collapse, Space, Row, Col, Button, Typography, Tag, Modal, Badge, Progress, Divider, Input, Select, Table, Dropdown } from 'antd';
import { TruckOutlined, SearchOutlined, ClearOutlined, DollarOutlined, ClockCircleOutlined, CheckCircleOutlined, DownOutlined, EyeOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { useLivrari } from '../hook/useLivrari';
import { useStateContext } from '../../../../../contexts/ContextProvider';

const { Text, Title } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

// Componentă pentru filtrele de căutare
const FiltersComponent = ({ 
  localSearchValue, 
  setLocalSearchValue, 
  filters,
  applyFilters,
  shipmentStatuses,
  couriers,
  handleKeyPress,
  handleApplyFilters,
  handleClearFilters,
  loading,
  isMobile 
}) => (
  <Card size="small" style={{ marginBottom: 16 }}>
    <Row gutter={[16, 16]} align="middle">
      <Col xs={24} sm={12} md={6} lg={5}>
        <Input
          placeholder="Căutare: furnizor, client, AWB..."
          prefix={<SearchOutlined />}
          value={localSearchValue}
          onChange={(e) => setLocalSearchValue(e.target.value)}
          onKeyPress={handleKeyPress}
          allowClear
          onClear={() => setLocalSearchValue('')}
        />
      </Col>
      <Col xs={24} sm={12} md={6} lg={4}>
        <Select
          placeholder="Status livrări..."
          mode="multiple"
          value={filters.status_id}
          onChange={(value) => applyFilters({ ...filters, status_id: value })}
          style={{ width: '100%' }}
          allowClear
        >
          {shipmentStatuses.map(status => (
            <Option key={status.id} value={status.id}>
              <Tag color={status.color} size="small">{status.label}</Tag>
            </Option>
          ))}
        </Select>
      </Col>
      <Col xs={24} sm={12} md={6} lg={4}>
        <Select
          placeholder="Curieri..."
          mode="multiple"
          value={filters.courier_id}
          onChange={(value) => applyFilters({ ...filters, courier_id: value })}
          style={{ width: '100%' }}
          allowClear
        >
          {couriers.map(courier => (
            <Option key={courier.id} value={courier.id}>
              {courier.name} ({courier.type})
            </Option>
          ))}
        </Select>
      </Col>
      <Col xs={24} sm={12} md={6} lg={3}>
        <Input
          placeholder="AWB..."
          value={filters.awb}
          onChange={(e) => applyFilters({ ...filters, awb: e.target.value })}
          onKeyPress={handleKeyPress}
          allowClear
        />
      </Col>
      <Col xs={24} sm={6} md={4} lg={4}>
        <Button 
          type="primary"
          icon={<SearchOutlined />} 
          onClick={handleApplyFilters}
          block={isMobile}
          loading={loading}
        >
          Filtrare
        </Button>
      </Col>
      <Col xs={24} sm={6} md={4} lg={4}>
        <Button 
          icon={<ClearOutlined />} 
          onClick={handleClearFilters}
          block={isMobile}
        >
          Reset
        </Button>
      </Col>
    </Row>
  </Card>
);

// Componentă pentru editarea AWB-ului
const AWBEditor = ({ shipment, updateShipmentAWB }) => {
  const [editing, setEditing] = useState(false);
  const [awbValue, setAwbValue] = useState(shipment.awb || '');

  const handleSave = async () => {
    const success = await updateShipmentAWB(shipment.id, awbValue);
    if (success) {
      setEditing(false);
    }
  };

  const handleCancel = () => {
    setAwbValue(shipment.awb || '');
    setEditing(false);
  };

  if (editing) {
    return (
      <Space>
        <Input
          value={awbValue}
          onChange={(e) => setAwbValue(e.target.value)}
          placeholder="Introduceți AWB..."
          style={{ width: 150 }}
          onPressEnter={handleSave}
        />
        <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSave}>
          Salvează
        </Button>
        <Button size="small" onClick={handleCancel}>
          Anulează
        </Button>
      </Space>
    );
  }

  return (
    <Space>
      <Text strong={!!shipment.awb} style={{ color: shipment.awb ? '#1890ff' : '#8c8c8c' }}>
        {shipment.awb || 'Fără AWB'}
      </Text>
      <Button 
        size="small" 
        type="link" 
        icon={<EditOutlined />} 
        onClick={() => setEditing(true)}
      >
        {shipment.awb ? 'Editează' : 'Adaugă'}
      </Button>
    </Space>
  );
};

function Livrari() {
  // State local
  const [isMobile, setIsMobile] = useState(false);
  const [selectedShipments, setSelectedShipments] = useState({});
  const [localSearchValue, setLocalSearchValue] = useState('');

  const { user } = useStateContext();

  // Hook pentru livrări
  const {
    shipments,
    loading,
    pagination,
    shipmentStatuses,
    couriers,
    overallSummary,
    filters,
    applyFilters,
    clearFilters,
    handleTableChange,
    updateShipmentStatus,
    updateShipmentAWB,
    createShipment
  } = useLivrari();

  // Detectare device mobile
  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Sincronizare state local cu filters din hook
  useEffect(() => {
    setLocalSearchValue(filters.search || '');
  }, [filters.search]);

  // Utility functions
  const getStatusColor = (statusId) => {
    const status = shipmentStatuses.find(option => option.id === statusId);
    return status ? status.color : '#8c8c8c';
  };

  const getStatusLabel = (statusId) => {
    const status = shipmentStatuses.find(option => option.id === statusId);
    return status ? status.label : 'Necunoscut';
  };

  const getCourierName = (courierId) => {
    const courier = couriers.find(c => c.id === courierId);
    return courier ? `${courier.name} (${courier.type})` : 'Necunoscut';
  };

  // Funcție pentru aplicarea filtrelor (manual trigger)
  const handleApplyFilters = () => {
    const newFilters = {
      ...filters,
      search: localSearchValue
    };
    applyFilters(newFilters);
  };

  // Handler pentru Enter key în input-uri
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  // Clear filters handler
  const handleClearFilters = () => {
    setLocalSearchValue('');
    clearFilters();
  };

  // Toggle shipment details
  const toggleShipmentDetails = (shipmentId) => {
    setSelectedShipments(prev => ({
      ...prev,
      [shipmentId]: !prev[shipmentId]
    }));
  };

  // Grupare shipments pe purchase_request_id
  const groupedShipments = useCallback(() => {
    const groups = {};
    shipments.forEach(shipment => {
      const key = shipment.purchase_request_id;
      if (!groups[key]) {
        groups[key] = {
          purchase_request_id: key,
          client: shipment.client,
          shipments: []
        };
      }
      groups[key].shipments.push(shipment);
    });
    return Object.values(groups);
  }, [shipments]);

  // Calculare sumar pentru un grup de shipments
  const calculateGroupSummary = useCallback((group) => {
    const totalShipments = group.shipments.length;
    const statusCounts = group.shipments.reduce((acc, shipment) => {
      const status = shipmentStatuses.find(s => s.id === shipment.status_id);
      if (status) {
        acc[status.id] = (acc[status.id] || 0) + 1;
      }
      return acc;
    }, {});

    const deliveredShipments = statusCounts[3] || 0; // Status 3 = livrat
    const progress = totalShipments > 0 ? (deliveredShipments / totalShipments) * 100 : 0;

    const totalProducts = group.shipments.reduce((acc, shipment) => 
      acc + (shipment.produse?.length || 0), 0
    );

    const totalWeight = group.shipments.reduce((acc, shipment) => 
      acc + (parseFloat(shipment.weight_kgs) || 0), 0
    );

    return {
      totalShipments,
      statusCounts,
      deliveredShipments,
      progress,
      totalProducts,
      totalWeight
    };
  }, [shipmentStatuses]);

  // Componentă pentru afișarea statusurilor cu contori
  const StatusCountsDisplay = ({ statusCounts, size = 'small', direction = 'horizontal' }) => {
    if (!statusCounts || Object.keys(statusCounts).length === 0) return null;
    
    const statusEntries = Object.entries(statusCounts)
      .map(([statusId, count]) => {
        const status = shipmentStatuses.find(s => s.id === parseInt(statusId));
        return status ? { ...status, count } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.id - b.id);

    const containerStyle = direction === 'vertical' 
      ? { display: 'flex', flexDirection: 'column', gap: '4px' }
      : { display: 'flex', flexWrap: 'wrap', gap: '4px' };

    return (
      <div style={containerStyle}>
        {statusEntries.map(status => (
          <Tag 
            key={status.id} 
            color={status.color} 
            size={size}
            style={{ 
              margin: 0,
              fontSize: size === 'small' ? '11px' : '12px',
              fontWeight: '500'
            }}
          >
            {status.label}: {status.count}
          </Tag>
        ))}
      </div>
    );
  };

  // Handler pentru schimbarea statusului unei livrări
  const handleShipmentStatusChange = async (shipmentId, newStatusId) => {
    await updateShipmentStatus(shipmentId, newStatusId);
  };

  // Componentă pentru sumar grup
  const GroupSummary = ({ group }) => {
    const summary = calculateGroupSummary(group);
    
    if (isMobile) {
      return (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Row gutter={[8, 8]}>
            <Col span={8}>
              <div style={{ textAlign: 'center', padding: '8px', background: '#f0f9ff', borderRadius: '6px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>{summary.totalShipments}</div>
                <div style={{ fontSize: '11px', color: '#666' }}>Livrări</div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center', padding: '8px', background: '#f0f9ff', borderRadius: '6px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>{summary.totalProducts}</div>
                <div style={{ fontSize: '11px', color: '#666' }}>Produse</div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center', padding: '8px', background: '#f0f9ff', borderRadius: '6px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#fa8c16' }}>
                  {summary.totalWeight.toFixed(1)}
                </div>
                <div style={{ fontSize: '11px', color: '#666' }}>Kg</div>
              </div>
            </Col>
          </Row>
          
          <div style={{ background: '#fafafa', padding: '8px', borderRadius: '6px' }}>
            <div style={{ marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
              Livrate: {summary.deliveredShipments}/{summary.totalShipments}
            </div>
            <Progress 
              percent={Math.round(summary.progress)} 
              size="small" 
              status={summary.progress === 100 ? 'success' : 'active'}
              showInfo={false}
            />
          </div>

          <div style={{ background: '#f6f8fa', padding: '8px', borderRadius: '6px' }}>
            <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: 'bold', color: '#666' }}>
              Statusuri livrări:
            </div>
            <StatusCountsDisplay 
              statusCounts={summary.statusCounts}
              size="small"
              direction="horizontal"
            />
          </div>
        </Space>
      );
    }

    return (
      <Space size="large" wrap>
        <Text strong>{summary.totalShipments} livrări</Text>
        <Text strong>{summary.totalProducts} produse</Text>
        <Text strong>Greutate: {summary.totalWeight.toFixed(1)} kg</Text>
        <div>
          <Text strong>Progres: </Text>
          <Progress 
            percent={Math.round(summary.progress)} 
            size="small" 
            status={summary.progress === 100 ? 'success' : 'active'}
            format={() => `${summary.deliveredShipments}/${summary.totalShipments}`}
          />
        </div>
        
        <div style={{ marginLeft: 16 }}>
          <StatusCountsDisplay 
            statusCounts={summary.statusCounts}
            size="small"
            direction="horizontal"
          />
        </div>
      </Space>
    );
  };

  // Componentă pentru produsele dintr-o livrare
  const ProductsDisplay = ({ products, isMobile }) => {
    if (!products || products.length === 0) return <Text>Nu există produse</Text>;

    const columns = [
      {
        title: 'Produs',
        dataIndex: 'produs_denumire',
        key: 'product_name',
        width: isMobile ? 200 : 400,
        render: (text, record) => (
          <div style={{ minWidth: isMobile ? 180 : 280 }}>
            <div>
              <Text strong style={{ fontSize: isMobile ? 12 : 14 }}>
                {text?.length > 60 ? `${text.substring(0, 60)}...` : text}
              </Text>
              <Tag className='ml-2' size="small" color="blue">SKU: {record.produs_sku}</Tag>
            </div>
          </div>
        ),
      },
      {
        title: 'Cantitate',
        dataIndex: 'product_qt',
        key: 'quantity',
        width: 80,
        align: 'center',
        render: (qty) => <Text>{qty}</Text>
      },
      {
        title: 'Preț',
        dataIndex: 'sale_price',
        key: 'price',
        width: 100,
        align: 'center',
        render: (price) => (
          <Text>{price ? `${price} MDL` : '-'}</Text>
        )
      },
      {
        title: 'Total',
        key: 'total',
        width: 100,
        align: 'center',
        render: (_, record) => {
          const total = (record.sale_price || 0) * (record.product_qt || 1);
          return <Text strong style={{ color: '#1890ff' }}>{total.toFixed(2)} MDL</Text>;
        }
      },
      {
        title: 'Status',
        dataIndex: 'status_label',
        key: 'status',
        width: 120,
        align: 'center',
        render: (label, record) => (
          <Tag color={record.status_color}>{label}</Tag>
        )
      }
    ];

    if (isMobile) {
      return (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {products.map((product, index) => (
            <Card 
              key={`${product.id}-${index}`}
              size="small"
              style={{ 
                borderLeft: `4px solid ${product.status_color}`,
                background: '#fafafa'
              }}
            >
              <Row gutter={[8, 8]}>
                <Col span={24}>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong style={{ fontSize: 14, display: 'block' }}>
                      {product.produs_denumire?.length > 50 
                        ? `${product.produs_denumire.substring(0, 50)}...` 
                        : product.produs_denumire}
                    </Text>
                    <Tag size="small" color="blue">SKU: {product.produs_sku}</Tag>
                    <Tag size="small" color={product.status_color}>{product.status_label}</Tag>
                  </div>
                  
                  <Row gutter={[8, 8]}>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 'bold' }}>
                          {product.product_qt}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Buc</div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1890ff' }}>
                          {product.sale_price || 0}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>MDL</div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 'bold', color: '#52c41a' }}>
                          {((product.sale_price || 0) * (product.product_qt || 1)).toFixed(0)}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Total MDL</div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          ))}
        </Space>
      );
    }

    return (
      <Table
        columns={columns}
        dataSource={products}
        rowKey={(record, index) => `${record.id}-${index}`}
        pagination={false}
        scroll={{ x: true }}
        size="small"
      />
    );
  };

  const groupedData = groupedShipments();

  return (
    <div style={{ padding: '12px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Filtre de căutare */}
      <FiltersComponent 
        localSearchValue={localSearchValue}
        setLocalSearchValue={setLocalSearchValue}
        filters={filters}
        applyFilters={applyFilters}
        shipmentStatuses={shipmentStatuses}
        couriers={couriers}
        handleKeyPress={handleKeyPress}
        handleApplyFilters={handleApplyFilters}
        handleClearFilters={handleClearFilters}
        loading={loading}
        isMobile={isMobile}
      />

      {/* Sumar general */}
      {overallSummary && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 8]} align="middle">
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                  {overallSummary.total_shipments}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Livrări</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                  {overallSummary.total_products}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Produse</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#fa8c16' }}>
                  {overallSummary.total_weight?.toFixed(1)} kg
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Greutate</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#722ed1' }}>
                  {overallSummary.unique_couriers}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Curieri</div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* Lista de livrări grupate */}
      <Card>
        <Title level={4} style={{ marginBottom: 16 }}>
          Livrări Grupate pe Cereri de Achiziție ({groupedData.length})
        </Title>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text>Se încarcă...</Text>
          </div>
        ) : (
          <>
            <Collapse 
              size={isMobile ? 'small' : 'default'}
              ghost={isMobile}
            >
              {groupedData.map((group) => (
                <Panel
                  header={
                    <div style={{ width: '100%' }}>
                      {isMobile ? (
                        <div>
                          <div style={{ marginBottom: 8 }}>
                            <Space wrap>
                              <TruckOutlined />
                              <Text strong style={{ fontSize: 14 }}>
                                {group.client?.denumire || 'Client necunoscut'}
                              </Text>
                              <Badge count={group.shipments?.length || 0} style={{ backgroundColor: '#1890ff' }} />
                            </Space>
                          </div>
                          <GroupSummary group={group} />
                        </div>
                      ) : (
                        <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                          <Col flex="auto">
                            <Space wrap>
                              <TruckOutlined />
                              <Text strong>{group.client?.denumire || 'Client necunoscut'}</Text>
                              <Tag color="green">Cerere #{group.purchase_request_id}</Tag>
                              <Badge count={group.shipments?.length || 0} style={{ backgroundColor: '#1890ff' }} />
                            </Space>
                          </Col>
                          <Col flex="none">
                            <GroupSummary group={group} />
                          </Col>
                        </Row>
                      )}
                    </div>
                  }
                  key={group.purchase_request_id}
                >
                  {/* Shipments pentru grup */}
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {group.shipments?.map(shipment => {
                      const isExpanded = selectedShipments[shipment.id];
                      
                      return (
                        <Card 
                          key={shipment.id}
                          size="small"
                          style={{ 
                            border: '1px solid #d9d9d9',
                            borderRadius: '8px',
                            borderLeft: `4px solid ${getStatusColor(shipment.status_id)}`
                          }}
                          title={
                            <Row justify="space-between" align="middle">
                              <Col>
                                <Space>
                                  <Button
                                    size="small"
                                    type="primary"
                                    onClick={() => toggleShipmentDetails(shipment.id)}
                                  >
                                    {isExpanded ? 'Ascunde' : 'Detalii'}
                                  </Button>
                                  <Dropdown
                                    menu={{
                                      items: shipmentStatuses.map(status => ({
                                        key: status.id,
                                        label: (
                                          <div 
                                            style={{ 
                                              padding: '8px 12px',
                                              color: status.color,
                                              fontWeight: status.id === shipment.status_id ? 'bold' : 'normal'
                                            }}
                                            onClick={() => status.id !== shipment.status_id && handleShipmentStatusChange(shipment.id, status.id)}
                                          >
                                            {status.label}
                                            {status.id === shipment.status_id && ' (actual)'}
                                          </div>
                                        ),
                                        disabled: status.id === shipment.status_id
                                      }))
                                    }}
                                    trigger={['click']}
                                    placement="bottomLeft"
                                  >
                                    <Button 
                                      size="small" 
                                      style={{ 
                                        borderColor: getStatusColor(shipment.status_id),
                                        color: getStatusColor(shipment.status_id)
                                      }}
                                    >
                                      {getStatusLabel(shipment.status_id)} <DownOutlined />
                                    </Button>
                                  </Dropdown>
                                </Space>
                              </Col>
                              <Col flex="auto">
                                <Space wrap style={{ marginLeft: 16 }}>
                                  <Text strong style={{ color: '#1890ff' }}>
                                    Livrare #{shipment.id}
                                  </Text>
                                  <Tag color="purple">
                                    Oferta #{shipment.offer_id}
                                  </Tag>
                                  <Text strong>
                                    {shipment.furnizor?.denumire || 'Furnizor necunoscut'}
                                  </Text>
                                  <Tag color="orange">
                                    {getCourierName(shipment.courier_id)}
                                  </Tag>
                                  <Badge 
                                    count={shipment.produse?.length || 0} 
                                    style={{ backgroundColor: '#52c41a' }}
                                    title="Numărul de produse"
                                  />
                                  {shipment.weight_kgs > 0 && (
                                    <Text strong>{shipment.weight_kgs} kg</Text>
                                  )}
                                </Space>
                              </Col>
                              <Col>
                                <AWBEditor 
                                  shipment={shipment}
                                  updateShipmentAWB={updateShipmentAWB}
                                />
                              </Col>
                            </Row>
                          }
                        >
                          {isExpanded && (
                            <div style={{ marginTop: 16 }}>
                              <Row gutter={[16, 16]}>
                                <Col xs={24} md={12}>
                                  <Card size="small" title="Informații Client">
                                    <Space direction="vertical" size="small">
                                      <Text><strong>Denumire:</strong> {shipment.client?.denumire}</Text>
                                      <Text><strong>Adresă juridică:</strong> {shipment.client?.adresa_juridica || 'Nu este specificată'}</Text>
                                      <Text><strong>Adresă livrare:</strong> {shipment.client?.shiping_address || 'Nu este specificată'}</Text>
                                      <Text><strong>Persoană contact:</strong> {shipment.client?.shiping_name || 'Nu este specificată'}</Text>
                                      <Text><strong>Telefon:</strong> {shipment.client?.shiping_phone || 'Nu este specificat'}</Text>
                                    </Space>
                                  </Card>
                                </Col>
                                <Col xs={24} md={12}>
                                  <Card size="small" title="Informații Furnizor">
                                    <Space direction="vertical" size="small">
                                      <Text><strong>Denumire:</strong> {shipment.furnizor?.denumire}</Text>
                                      <Text><strong>Adresă:</strong> {shipment.furnizor?.adresa_juridica || 'Nu este specificată'}</Text>
                                      <Text><strong>Telefon:</strong> {shipment.furnizor?.telefon || 'Nu este specificat'}</Text>
                                      <Text><strong>Email:</strong> {shipment.furnizor?.email || 'Nu este specificat'}</Text>
                                    </Space>
                                  </Card>
                                </Col>
                              </Row>
                              
                              <Divider style={{ margin: '16px 0' }} />
                              
                              <Card size="small" title={`Produse (${shipment.produse?.length || 0})`}>
                                <ProductsDisplay 
                                  products={shipment.produse || []} 
                                  isMobile={isMobile}
                                />
                              </Card>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </Space>
                </Panel>
              ))}
            </Collapse>
            
            {/* Paginație */}
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Space>
                <Button 
                  disabled={pagination.current <= 1}
                  onClick={() => handleTableChange({ ...pagination, current: pagination.current - 1 })}
                >
                  Anterior
                </Button>
                <span>
                  Pagina {pagination.current} din {Math.ceil(pagination.total / pagination.pageSize)}
                </span>
                <Button 
                  disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                  onClick={() => handleTableChange({ ...pagination, current: pagination.current + 1 })}
                >
                  Următor
                </Button>
              </Space>
            </div>
          </>
        )}
      </Card>

      {/* Stiluri CSS pentru componente mobile */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media (max-width: 768px) {
            .ant-collapse-header {
              padding: 12px 16px !important;
            }
            .ant-card-head {
              padding: 0 12px;
              min-height: 40px;
            }
            .ant-card-body {
              padding: 12px;
            }
            .ant-table {
              font-size: 12px;
            }
            .ant-table-thead > tr > th {
              padding: 8px 4px;
              font-size: 11px;
            }
            .ant-table-tbody > tr > td {
              padding: 8px 4px;
            }
          }
        `
      }} />
    </div>
  );
}

export default Livrari